// Global utility functions

// Debounce function
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// Throttle function
function throttle(fn, delay) {
  let last = 0;
  return (...args) => {
    const now = new Date().getTime();
    if (now - last < delay) {
      return;
    }
    last = now;
    return fn(...args);
  };
}

// Fetch API wrapper for cart operations
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

// Trap focus within element (for modals, drawers)
function trapFocus(container, elementToFocus = container) {
  const focusableElements = Array.from(
    container.querySelectorAll(
      'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
    )
  );

  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  elementToFocus.focus();

  function trapFocusHandlers(e) {
    if (e.code.toUpperCase() !== 'TAB') return;

    if (e.shiftKey && document.activeElement === firstFocusable) {
      lastFocusable.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === lastFocusable) {
      firstFocusable.focus();
      e.preventDefault();
    }
  }

  document.addEventListener('keydown', trapFocusHandlers);

  return {
    removeListeners: () => {
      document.removeEventListener('keydown', trapFocusHandlers);
    }
  };
}

// Remove focus trap
function removeTrapFocus(elementToFocus = null) {
  if (elementToFocus) {
    elementToFocus.focus();
  }
  document.removeEventListener('keydown', trapFocusHandlers);
}

// Serialize form data
function serializeForm(form) {
  const formData = new FormData(form);
  const data = {};
  for (let [key, value] of formData) {
    if (data[key]) {
      if (!Array.isArray(data[key])) {
        data[key] = [data[key]];
      }
      data[key].push(value);
    } else {
      data[key] = value;
    }
  }
  return data;
}

// Pagination observer for infinite scroll (optional)
class IntersectionObserverController {
  constructor(observerCallback, rootMargin = '0px') {
    this.observerCallback = observerCallback;
    this.observers = new Map();
    this.rootMargin = rootMargin;
  }

  observe(element, callback) {
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            callback(entry);
          }
        });
      },
      { rootMargin: this.rootMargin }
    );

    observer.observe(element);
    this.observers.set(element, observer);
  }

  unobserve(element) {
    const observer = this.observers.get(element);
    if (observer) {
      observer.disconnect();
      this.observers.delete(element);
    }
  }

  unobserveAll() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();
  }
}

// Export for use in other scripts
window.thangaUtils = {
  debounce,
  throttle,
  fetchConfig,
  trapFocus,
  removeTrapFocus,
  serializeForm,
  IntersectionObserverController
};
